import { useState, type FormEvent, type ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { Plus } from "lucide-react";

import { libraryService, type Author, type Book, type BookCategory } from "../../services/libraryService";
import { apiErrorMessage } from "../../utils/apiError";
import { ConfirmDialog, type ConfirmRequest } from "../ConfirmDialog";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ClientRegister, type RegisterColumn } from "../register/ClientRegister";
import { Notice, deleteErrorText, type NoticeState } from "../register/RecordParts";
import { byNumber, byText, reversed, type RegisterConfig } from "../register/registerLogic";

type CategoryRow = BookCategory & { titles: number };
type AuthorRow = Author & { titles: number };

const categoryConfig: RegisterConfig<CategoryRow> = {
  search: (row) => [row.name, row.description].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "unused", label: "No titles", match: (row) => row.titles === 0 },
  ],
  sorts: [
    { key: "name", label: "Name (A–Z)", compare: byText<CategoryRow>((row) => row.name) },
    { key: "-titles", label: "Most titles", compare: reversed(byNumber<CategoryRow>((row) => row.titles)) },
  ],
};
const authorConfig: RegisterConfig<AuthorRow> = {
  search: (row) => [row.full_name, row.nationality].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "unused", label: "No titles", match: (row) => row.titles === 0 },
  ],
  sorts: [
    { key: "name", label: "Surname (A–Z)", compare: byText<AuthorRow>((row) => `${row.last_name} ${row.first_name}`) },
    { key: "-titles", label: "Most titles", compare: reversed(byNumber<AuthorRow>((row) => row.titles)) },
  ],
};

/** The lists the catalogue files titles under. Something in use is corrected, not removed. */
export function AuthorsCategoriesPanel({ books, authors, categories, isLoading, onChanged }: {
  books: Book[];
  authors?: Author[];
  categories?: BookCategory[];
  isLoading: boolean;
  onChanged: () => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [editingCategory, setEditingCategory] = useState<{ record?: BookCategory } | null>(null);
  const [editingAuthor, setEditingAuthor] = useState<{ record?: Author } | null>(null);
  const [dialog, setDialog] = useState<ConfirmRequest | null>(null);
  const categoryRows = categories?.map((row) => ({ ...row, titles: books.filter((book) => book.category === row.id).length }));
  const authorRows = authors?.map((row) => ({ ...row, titles: books.filter((book) => book.authors.includes(row.id)).length }));
  const done = (text: string) => {
    setNotice({ tone: "success", text });
    onChanged();
  };
  const remove = useMutation({
    mutationFn: ({ kind, id }: { kind: "category" | "author"; id: number }) => (kind === "category" ? libraryService.deleteCategory(id) : libraryService.deleteAuthor(id)),
    onSuccess: (_result, variables) => {
      setDialog(null);
      setEditingCategory(null);
      setEditingAuthor(null);
      done(variables.kind === "category" ? "Category removed." : "Author removed.");
    },
    onError: (error) => {
      setDialog(null);
      setNotice({ tone: "error", text: deleteErrorText(error) });
    },
  });

  const categoryColumns: RegisterColumn<CategoryRow>[] = [
    { header: "Category", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name },
    { header: "About it", cell: (row) => row.description || <span className="text-gray-300">—</span>, exportValue: (row) => row.description ?? "", className: "min-w-[14rem] whitespace-normal text-gray-600" },
    { header: "Titles", cell: (row) => row.titles, exportValue: (row) => row.titles, align: "right" },
  ];
  const authorColumns: RegisterColumn<AuthorRow>[] = [
    { header: "Author", cell: (row) => <span className="font-medium text-gray-900">{row.full_name}</span>, exportValue: (row) => row.full_name },
    { header: "Nationality", cell: (row) => row.nationality || <span className="text-gray-300">—</span>, exportValue: (row) => row.nationality ?? "", className: "text-gray-700" },
    { header: "Titles", cell: (row) => row.titles, exportValue: (row) => row.titles, align: "right" },
  ];
  const removeButton = (kind: "category" | "author", id: number, name: string, titles: number) =>
    titles === 0 ? (
      <button
        type="button"
        onClick={() => setDialog({ title: `Remove ${kind}?`, body: <p><strong>{name}</strong> has no titles, so it can be removed.</p>, confirmLabel: `Remove ${kind}`, tone: "danger", onConfirm: () => remove.mutate({ kind, id }) })}
        className="mr-auto rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
      >
        Remove
      </button>
    ) : (
      <p className="mr-auto text-xs text-gray-500">Filed under {titles} {titles === 1 ? "title" : "titles"}, so it stays.</p>
    );

  return (
    <div className="space-y-6">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Categories"
        description="The sections of the catalogue, e.g. Fiction, Science, Revision."
        actions={<button type="button" onClick={() => setEditingCategory({})} className="btn-primary h-8 min-h-8 px-3 text-xs"><Plus className="mr-1.5 h-3.5 w-3.5" /> New category</button>}
        rows={categoryRows}
        config={categoryConfig}
        columns={categoryColumns}
        rowKey={(row) => row.id}
        onOpen={(row) => setEditingCategory({ record: row })}
        openLabel={() => "Edit"}
        noun={["category", "categories"]}
        exportName={{ file: "library-categories", title: "Library Categories" }}
        isLoading={isLoading}
        emptyText="No categories yet."
      />
      <ClientRegister
        title="Authors"
        description="Everyone the catalogue lists as an author."
        actions={<button type="button" onClick={() => setEditingAuthor({})} className="btn-primary h-8 min-h-8 px-3 text-xs"><Plus className="mr-1.5 h-3.5 w-3.5" /> New author</button>}
        rows={authorRows}
        config={authorConfig}
        columns={authorColumns}
        rowKey={(row) => row.id}
        onOpen={(row) => setEditingAuthor({ record: row })}
        openLabel={() => "Edit"}
        noun={["author", "authors"]}
        exportName={{ file: "library-authors", title: "Library Authors" }}
        isLoading={isLoading}
        emptyText="No authors yet."
      />

      {editingCategory && !dialog && (
        <SimpleForm
          id="category-form"
          title={editingCategory.record ? `Edit ${editingCategory.record.name}` : "New category"}
          fields={[
            { name: "name", label: "Name", value: editingCategory.record?.name ?? "", required: true },
            { name: "description", label: "What goes here", value: editingCategory.record?.description ?? "", long: true },
          ]}
          extra={editingCategory.record && removeButton("category", editingCategory.record.id, editingCategory.record.name, categoryRows?.find((row) => row.id === editingCategory.record!.id)?.titles ?? 0)}
          save={(data) => (editingCategory.record ? libraryService.updateCategory(editingCategory.record.id, data) : libraryService.createCategory({ ...data, color: "#3B82F6" }))}
          onClose={() => setEditingCategory(null)}
          onDone={() => {
            setEditingCategory(null);
            done("Category saved.");
          }}
        />
      )}
      {editingAuthor && !dialog && (
        <SimpleForm
          id="author-form"
          title={editingAuthor.record ? `Edit ${editingAuthor.record.full_name}` : "New author"}
          fields={[
            { name: "first_name", label: "First name", value: editingAuthor.record?.first_name ?? "", required: true },
            { name: "last_name", label: "Surname", value: editingAuthor.record?.last_name ?? "", required: true },
            { name: "middle_name", label: "Middle name", value: editingAuthor.record?.middle_name ?? "" },
            { name: "nationality", label: "Nationality", value: editingAuthor.record?.nationality ?? "Uganda" },
          ]}
          extra={editingAuthor.record && removeButton("author", editingAuthor.record.id, editingAuthor.record.full_name, authorRows?.find((row) => row.id === editingAuthor.record!.id)?.titles ?? 0)}
          save={(data) => (editingAuthor.record ? libraryService.updateAuthor(editingAuthor.record.id, data) : libraryService.createAuthor(data))}
          onClose={() => setEditingAuthor(null)}
          onDone={() => {
            setEditingAuthor(null);
            done("Author saved.");
          }}
        />
      )}
      <ConfirmDialog request={dialog} onClose={() => setDialog(null)} busy={remove.isPending} />
    </div>
  );
}

function SimpleForm({ id, title, fields, extra, save, onClose, onDone }: {
  id: string;
  title: string;
  fields: { name: string; label: string; value: string; required?: boolean; long?: boolean }[];
  extra?: ReactNode;
  save: (data: Record<string, string | null>) => Promise<unknown>;
  onClose: () => void;
  onDone: () => void;
}) {
  const mutation = useMutation({ mutationFn: save, onSuccess: onDone });
  return (
    <FormModal
      id={id}
      title={title}
      busy={mutation.isPending}
      error={mutation.isError ? apiErrorMessage(mutation.error, "It could not be saved.") : undefined}
      saveLabel="Save"
      onClose={onClose}
      onSubmit={(event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        mutation.mutate(Object.fromEntries(fields.map((field) => [field.name, String(data.get(field.name) ?? "").trim() || null])));
      }}
    >
      <FormSection title="Details" divided={false} columns={fields.length > 2 ? 2 : 1}>
        {fields.map((field, index) => (
          <Field key={field.name} label={field.label} optional={!field.required}>
            {field.long ? (
              <textarea name={field.name} rows={3} defaultValue={field.value} className="input mt-1" />
            ) : (
              <input name={field.name} required={field.required} autoFocus={index === 0} defaultValue={field.value} className="input mt-1" />
            )}
          </Field>
        ))}
      </FormSection>
      {extra && <div className="flex items-center border-t border-gray-100 pt-3">{extra}</div>}
    </FormModal>
  );
}
